import { SelfServiceUserType } from '@/layers/support/authz/userType';
import logger from '@/layers/support/monitoring/logger';
import { UserType } from '@/types/modelEnums';
import { ValidProfiles } from '@/types/models';
import { SubscriptionParams } from '@/types/users';
import { fetchWrapper } from '../utils/fetchWrapper';
import { createPatientByUserId, fetchPatientByUserId } from './patients';
import { createProviderByUserId, fetchProviderByUserId } from './providers';
import { createSponsorByUserId, fetchSponsorByUserId } from './sponsors';

const API_URL = '/api/users';
const LOG_MODULE = 'FE [Presentation / Services / users]';

export const getProfile = async (
  userId: string,
  userType: UserType,
): Promise<ValidProfiles | null> => {
  logger.debug(`${LOG_MODULE} getProfile: begin`, { userId, userType });

  try {
    let profile: ValidProfiles | null = null;

    switch (userType) {
      case UserType.PROVIDER:
        profile = await fetchProviderByUserId(userId);
        if (!profile) {
          profile = await createProviderByUserId({ user_id: userId });
        }
        break;

      case UserType.PATIENT:
        profile = await fetchPatientByUserId(userId);
        if (!profile) {
          profile = await createPatientByUserId({ user_id: userId });
        }
        break;

      case UserType.SPONSOR:
        profile = await fetchSponsorByUserId(userId);
        if (!profile) {
          profile = await createSponsorByUserId({ user_id: userId });
        }
        break;

      // Add cases for other profile types as needed
      default:
        throw new Error(`Unsupported user type: ${userType}`);
    }

    profile = enhanceProfileWithType(profile, userType);

    logger.debug(`${LOG_MODULE} getProfile: end`, { profile });
    return profile;
  } catch (error) {
    logger.error(`${LOG_MODULE} getProfile`, { error });
    throw error;
  }
};

/**
 * Enhances a raw profile object with the specified UserType.
 * @param profile - The raw profile object fetched from the backend.
 * @param type - The UserType to assign to the profile.
 * @returns The profile object augmented with the type property.
 */
const enhanceProfileWithType = <T>(profile: T, type: UserType): T & { type: UserType } => {
  return { ...profile, type };
};

export const subscribeUser = async (
  email: string,
  params: SubscriptionParams,
): Promise<boolean> => {
  logger.debug(`${LOG_MODULE} subscribeUser: begin`, { email, params });

  try {
    const result = await fetchWrapper.post<boolean>(`${API_URL}/subscribe`, { email, params });

    logger.debug(`${LOG_MODULE} subscribeUser: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} subscribeUser: error`, { error });
    throw error;
  }
};

/** Sets the signed-in user's own type. A 409 means it is already set, which is also done. */
export const setUserType = async (type: SelfServiceUserType): Promise<void> => {
  logger.debug(`${LOG_MODULE} setUserType: begin`, { type });

  const response = await fetch(`${API_URL}/type`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type }),
  });
  if (!response.ok && response.status !== 409) {
    logger.error(`${LOG_MODULE} setUserType: error`, { status: response.status });
    throw new Error(`Failed to set user type (${response.status})`);
  }

  logger.debug(`${LOG_MODULE} setUserType: end`, { status: response.status });
};
